import React, { memo, useEffect, useRef, useState } from 'react';
import { FilesTooltip, useFilesConfig } from './config';
import { CATEGORY_ICONS_LARGE } from './constants';
import { categorizeAsset, isPreviewableAsset } from './fileTypes';
import type { Asset } from './types';
import { formatCreatedDate } from './utils';

interface FileCardProps {
    asset: Asset;
    thumbnailUrl?: string;
    onPreviewFile: (asset: Asset) => void;
    isSelected?: boolean;
    onToggleSelect?: (assetId: string) => void;
    selectionMode?: boolean;
    onThumbnailError?: (assetId: string) => void;
    /** Shows a "Processing…" badge - set while a CBCT job runs for this asset. */
    isProcessing?: boolean;
}

const FileCard: React.FC<FileCardProps> = memo(
    ({
        asset,
        thumbnailUrl,
        onPreviewFile,
        isSelected,
        onToggleSelect,
        selectionMode,
        onThumbnailError,
        isProcessing
    }) => {
        const { showUploadedBy, cbctProcessingEnabled } = useFilesConfig();
        const [imageError, setImageError] = useState(false);
        const [isNearViewport, setIsNearViewport] = useState(false);
        const cardRef = useRef<HTMLDivElement>(null);
        const canPreview = isPreviewableAsset(asset, { cbctProcessing: cbctProcessingEnabled });
        const category = categorizeAsset(asset.name);
        const isImage = category === 'Images';
        const assetKey = String(asset.assetId);
        const dateText = formatCreatedDate(asset.createdAt);

        useEffect(() => {
            const el = cardRef.current;
            if (!el || isNearViewport) return;

            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setIsNearViewport(true);
                        observer.disconnect();
                    }
                },
                { rootMargin: '200px' }
            );

            observer.observe(el);
            return () => observer.disconnect();
        }, [isNearViewport]);

        const handleImgError = () => {
            if (onThumbnailError) {
                onThumbnailError(assetKey);
            } else {
                setImageError(true);
            }
        };

        const renderThumbnail = () => {
            if (isImage && isNearViewport && thumbnailUrl && !imageError) {
                return (
                    <img
                        src={thumbnailUrl}
                        alt={asset.name}
                        className="w-full h-full object-cover"
                        decoding="async"
                        onError={handleImgError}
                    />
                );
            }
            if (isImage && !imageError) {
                return <div className="w-full h-full bg-gray-200 animate-pulse" />;
            }
            return CATEGORY_ICONS_LARGE[category] || CATEGORY_ICONS_LARGE['Others'];
        };

        return (
            <div
                ref={cardRef}
                className={`relative rounded-lg border overflow-hidden bg-white ${
                    canPreview || selectionMode ? 'cursor-pointer' : 'cursor-default'
                } ${isSelected ? 'border-gray-900 ring-2 ring-gray-900/20' : 'border-gray-200'}`}
                onClick={() => {
                    if (selectionMode && onToggleSelect) {
                        onToggleSelect(assetKey);
                    } else if (canPreview) {
                        onPreviewFile(asset);
                    }
                }}
            >
                {selectionMode && onToggleSelect && (
                    <label
                        className="absolute top-1.5 left-1.5 z-10 cursor-pointer"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <input
                            type="checkbox"
                            checked={isSelected ?? false}
                            onChange={() => onToggleSelect(assetKey)}
                            aria-label={`Select ${asset.name}`}
                            className="w-3.5 h-3.5 rounded border-gray-300 text-gray-900 focus:ring-gray-900 cursor-pointer"
                        />
                    </label>
                )}

                {isSelected && (
                    <div className="absolute inset-0 z-[1] pointer-events-none bg-gray-900/10" />
                )}

                {isProcessing && (
                    <div className="absolute top-1.5 right-1.5 z-10 flex items-center gap-1 bg-white/90 border border-gray-200 text-gray-600 text-xs font-medium px-2 py-0.5 rounded-full pointer-events-none">
                        <span className="h-2.5 w-2.5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                        Processing…
                    </div>
                )}

                <div className="aspect-square bg-gray-50 overflow-hidden relative flex items-center justify-center">
                    {renderThumbnail()}
                </div>

                <div className="px-2 py-1.5">
                    <FilesTooltip content={asset.name}>
                        <p className="text-xs text-gray-700 font-medium truncate">{asset.name}</p>
                    </FilesTooltip>
                    {dateText && (
                        <p className="text-[10px] text-gray-400 mt-0.5">
                            {dateText}
                            {showUploadedBy && asset.source === 'gcs' && asset.uploadedBy && (
                                <> · By {asset.uploadedBy.name}</>
                            )}
                        </p>
                    )}
                </div>
            </div>
        );
    }
);

FileCard.displayName = 'FileCard';

export { FileCard };
